import { Navigate, Route } from 'react-router-dom';
import ProtectedRoute from 'shared/routes/Protected-Route';
import { PERMISSIONS } from 'platform/rbac/Permissions';
import LegacyTenantPathRedirect from 'shared/routes/LegacyTenantPathRedirect';
import SystemRegisterPage from './pages/SystemRegisterPage';
import ItAssetRegisterPage from './pages/ItAssetRegisterPage';
import AccessProvisionPage from './pages/AccessProvisionPage';
import AccessProvisionViewPage from './entities/access-provision/AccessProvisionViewPage';
import AssetViewPage from './entities/asset-register/AssetViewPage';
import SopPage from './pages/SopPage';
import ManageSystemDetailsPage from './pages/ManageSystemDetailsPage';
import ManageDetailsGroupPage from './pages/ManageDetailsGroupPage';
import SoftwarePage from './entities/software/SoftwarePage';
import SoftwareReportViewPage from './entities/software/SoftwareReportViewPage';
import RegisterSoftwareWizardPage from './entities/software/register/RegisterSoftwareWizardPage';
import BusinessUnitsPage from './entities/business-unit/BusinessUnitsPage';
import BusinessProcessesPage from './entities/business-process/BusinessProcessesPage';
import SystemOwnersPage from './entities/system-owner/SystemOwnersPage';
import VendorsPage from './entities/vendor/VendorsPage';
import RegulationsPage from './entities/regulation/RegulationsPage';
import DataInformationPage from './entities/data-information/DataInformationPage';
import ArtificialIntelligenceDetailsViewPage from './entities/data-information/ArtificialIntelligenceDetailsViewPage';
import DataSourcesPage from './entities/data-source/DataSourcesPage';
import DataSourceDetailsViewPage from './entities/data-source/DataSourceDetailsViewPage';
import RiskRegisterPage from './entities/risk-register/RiskRegisterPage';
import RiskRegisterViewPage from './entities/risk-register/RiskRegisterViewPage';
import DocumentsPage from './entities/document/DocumentsPage';
import DocumentsDetailsViewPage from './entities/document/DocumentsDetailsViewPage';
import ReviewsPage from './entities/review/ReviewsPage';
import IncidentsPage from './entities/incident/IncidentsPage';
import IncidentViewPage from './entities/incident/IncidentViewPage';
import MonitoringReviewViewPage from './entities/monitoring-review/MonitoringReviewViewPage';
import SoftwareRiskViewPage from './entities/software-risk/SoftwareRiskViewPage';
import ApprovalsPage from './entities/approval/ApprovalsPage';
import DeploymentStatusesPage from './entities/deployment-status/DeploymentStatusesPage';
import ChangeManagementsPage from './entities/change-management/ChangeManagementsPage';
import ChangeManagementViewPage from './entities/change-management/ChangeManagementViewPage';
import BusinessCaseViewPage from './entities/business-case/BusinessCaseViewPage';
import SoftwareRecordDetailsViewPage from './shared/SoftwareRecordDetailsViewPage';

function withTenant(path, element) {
  const clean = String(path).replace(/^\//, '');
  return [
    <Route key={`t-${clean}`} path={`/:tenantKey/${clean}`} element={element} />,
    <Route key={`l-${clean}`} path={`/${clean}`} element={<LegacyTenantPathRedirect />} />,
  ];
}

const VIEW = PERMISSIONS.systemRegister;
const viewSystems = [VIEW.viewSystems];
const viewSystemDetails = [VIEW.viewSystemDetails];
const viewRiskRegister = [VIEW.viewRiskRegister, VIEW.viewSystemDetails];
const viewAssetRegister = [VIEW.viewAssetRegister];
const viewSop = [VIEW.viewSop];
const viewChangeManagement = [VIEW.viewChangeManagement, VIEW.viewSystemDetails];
const viewMonitoringReview = [VIEW.viewMonitoringReview, VIEW.viewSystemDetails];
const viewIncidentManagement = [VIEW.viewIncidentManagement, VIEW.viewSystemDetails];
const viewAccessProvision = [VIEW.viewAccessProvision];

const IT_GOVERNANCE_ADMIN_ROLES = ['ADMIN', 'SYSTEMADMIN'];

function guard(path, element, allowedPermissions) {
  return withTenant(
    path,
    <ProtectedRoute
      element={element}
      allowedRoles={IT_GOVERNANCE_ADMIN_ROLES}
      allowedPermissions={allowedPermissions}
    />
  );
}

/** SYSTEM REGISTER (333) — paths stay under /{tenantName}/system-register/... */
export const systemRegisterRoutes = (
  <>
    {withTenant('/system-register', <ProtectedRoute element={<SystemRegisterPage />} />)}
    {guard('/system-register/it-asset-register', <ItAssetRegisterPage />, viewAssetRegister)}
    {guard('/system-register/access-provisions', <AccessProvisionPage />, viewAccessProvision)}
    {guard('/system-register/access-provisions/view', <AccessProvisionViewPage />, viewAccessProvision)}
    {guard('/system-register/it-asset-register/view', <AssetViewPage />, viewAssetRegister)}
    {guard('/system-register/sop', <SopPage />, viewSop)}
    {guard('/system-register/manage-details', <ManageSystemDetailsPage />, viewSystemDetails)}
    {guard('/system-register/manage-details/:group', <ManageDetailsGroupPage />, viewSystemDetails)}
    {guard('/system-register/software', <SoftwarePage />, viewSystems)}
    {guard('/system-register/software/register', <RegisterSoftwareWizardPage />, viewSystems)}
    {guard('/system-register/software/:id/report', <SoftwareReportViewPage />, viewSystems)}
    {guard('/system-register/software/:id/details', <RegisterSoftwareWizardPage />, viewSystems)}
    {guard('/system-register/business-units', <BusinessUnitsPage />, viewSystemDetails)}
    {guard('/system-register/business-processes', <BusinessProcessesPage />, viewSystemDetails)}
    {guard('/system-register/system-owners', <SystemOwnersPage />, viewSystemDetails)}
    {guard('/system-register/vendors', <VendorsPage />, viewSystemDetails)}
    {guard('/system-register/regulations', <RegulationsPage />, viewSystemDetails)}
    {guard('/system-register/data-information', <DataInformationPage />, viewSystemDetails)}
    {guard('/system-register/data-information/view', <ArtificialIntelligenceDetailsViewPage />, viewSystemDetails)}
    {guard('/system-register/data-sources', <DataSourcesPage />, viewSystemDetails)}
    {guard('/system-register/data-sources/view', <DataSourceDetailsViewPage />, viewSystemDetails)}
    {guard('/system-register/risk-register', <RiskRegisterPage />, viewRiskRegister)}
    {guard('/system-register/risk-register/view', <RiskRegisterViewPage />, viewRiskRegister)}
    {guard(
      '/system-register/risk-assessments',
      <Navigate to="/system-register/risk-register" replace />,
      viewRiskRegister
    )}
    {guard(
      '/system-register/risk-assessments/view',
      <SoftwareRecordDetailsViewPage sectionKey="risk-assessments" />,
      viewRiskRegister
    )}
    {guard('/system-register/documents', <DocumentsPage />, viewSystemDetails)}
    {guard('/system-register/documents/view', <DocumentsDetailsViewPage />, viewSystemDetails)}
    {guard('/system-register/reviews', <ReviewsPage />, viewMonitoringReview)}
    {guard('/system-register/reviews/view', <MonitoringReviewViewPage />, viewMonitoringReview)}
    {guard('/system-register/incidents', <IncidentsPage />, viewIncidentManagement)}
    {guard('/system-register/incidents/view', <IncidentViewPage />, viewIncidentManagement)}
    {guard('/system-register/incidents/details', <IncidentViewPage />, viewIncidentManagement)}
    {guard('/system-register/change-managements', <ChangeManagementsPage />, viewChangeManagement)}
    {guard('/system-register/change-managements/view', <ChangeManagementViewPage />, viewChangeManagement)}
    {guard('/system-register/change-managements/details', <ChangeManagementViewPage />, viewChangeManagement)}
    {guard('/system-register/business-cases/view', <BusinessCaseViewPage />, viewSystems)}
    {guard('/system-register/business-cases/details', <BusinessCaseViewPage />, viewSystems)}
    {guard('/system-register/monitoring-reviews/view', <MonitoringReviewViewPage />, viewMonitoringReview)}
    {guard('/system-register/software-risks/view', <SoftwareRiskViewPage />, viewRiskRegister)}
    {guard('/system-register/approvals', <ApprovalsPage />, viewSystemDetails)}
    {guard(
      '/system-register/approvals/view',
      <SoftwareRecordDetailsViewPage sectionKey="approvals" />,
      viewSystemDetails
    )}
    {guard('/system-register/deployment-statuses', <DeploymentStatusesPage />, viewSystemDetails)}
    {guard(
      '/system-register/deployment-statuses/view',
      <SoftwareRecordDetailsViewPage sectionKey="deployment-statuses" />,
      viewSystemDetails
    )}
  </>
);

export default systemRegisterRoutes;
